import React from 'react'
import { CONTACT_CONFIG } from '../../config/constants.js'
import { BOOKING_LABEL, type CopyLang } from '../../data/workshop-packages.js'
import { useLocale } from '../../hooks/i18n/useLocale.js'
import { ServiceIcon } from '../ServiceIcon'
import { useLandingContent } from '../../hooks/i18n/usePageContent.js'
import * as TOKENS from '../chat/design-tokens'

interface ServicesSectionProps {
  servicesRef: React.RefObject<HTMLElement>
}

const sectionShell = `w-full scroll-mt-24 px-6 py-20 md:px-12 border-t ${TOKENS.BORDER.default} bg-transparent`
const eyebrow = `${TOKENS.TEXT_SIZE.xs} font-mono uppercase tracking-[0.25em] ${TOKENS.TEXT.muted}`

export const ServicesSection: React.FC<ServicesSectionProps> = ({
  servicesRef,
}) => {
  const { services } = useLandingContent()
  const { locale } = useLocale()
  const lang: CopyLang = locale === 'nb' ? 'nb' : 'en'

  return (
    <section
      ref={servicesRef}
      id="services"
      aria-labelledby="services-heading"
      className={`${sectionShell} relative overflow-hidden`}
    >
      <div className="relative z-10 mx-auto flex max-w-6xl flex-col gap-14">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="flex max-w-2xl flex-col gap-3">
            <span className={eyebrow}>{services.eyebrow}</span>
            <h2
              id="services-heading"
              className={`text-4xl font-semibold tracking-tighter md:text-5xl ${TOKENS.TEXT.primary}`}
            >
              {services.title}
            </h2>
            <p className={`text-lg font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
              {services.body}
            </p>
          </div>
          <a
            href={CONTACT_CONFIG.SCHEDULING.BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={`
              group relative shrink-0 inline-flex items-center justify-center gap-2
              overflow-hidden self-start rounded-none border px-6 py-3
              ${TOKENS.TEXT_SIZE.sm} font-medium tracking-wide
              ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.raised}
              transition-colors duration-150 hover:bg-muted hover:border-foreground/30
              ${TOKENS.INTERACTION.focusRing}
            `}
          >
            <span className={TOKENS.INTERACTION.shimmer} />
            <span className="relative">{BOOKING_LABEL[lang]}</span>
          </a>
        </div>

        <div
          className="grid grid-cols-1 divide-y border-t border-b border-border md:grid-cols-3 md:divide-y-0 md:divide-x divide-border"
          role="list"
        >
          {services.items.map((svc, index) => (
            <article
              key={svc.title}
              role="listitem"
              className={`
                group flex flex-col gap-5 py-8
                transition-colors duration-150
                ${index === 0 ? 'md:pr-8' : index === 1 ? 'md:px-8' : 'md:pl-8'}
              `}
            >
              <div
                className={`
                  relative h-11 w-11 overflow-hidden border rounded-none
                  bg-secondary ${TOKENS.BORDER.default}
                  transition-colors duration-150 group-hover:bg-muted
                `}
              >
                <ServiceIcon iconType={svc.icon as 'book' | 'people' | 'code'} color="orange" />
              </div>

              <div className="flex flex-col gap-2">
                <h3 className={`text-base font-semibold tracking-tight ${TOKENS.TEXT.primary}`}>
                  {'href' in svc && svc.href ? (
                    <a href={svc.href} className="no-underline hover:underline">
                      {svc.title.charAt(0).toUpperCase() + svc.title.slice(1)}
                    </a>
                  ) : (
                    svc.title.charAt(0).toUpperCase() + svc.title.slice(1)
                  )}
                </h3>
                <p className={`text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
                  {svc.description}
                </p>
              </div>

              <ul className={`mt-auto flex flex-col ${TOKENS.GAP.sm} pt-4`}>
                {svc.items.map(item => (
                  <li
                    key={item}
                    className={`flex items-center gap-2.5 ${TOKENS.TEXT_SIZE.xs} font-mono tracking-wide ${TOKENS.TEXT.muted}`}
                  >
                    <span className={`h-1 w-1 shrink-0 rounded-full ${TOKENS.BRAND.accentBg}`} aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}
